import type {
  Attempt,
  HumanReviewDecision,
  HumanReviewRequest,
} from '@/lib/admin/client';
import {
  humanReviewDecisionActor,
  humanReviewSubjectRef,
} from '@/lib/admin/client';

import { decisionResultBadgeVariant } from './badgeVariants';
import { payloadTitle } from './payloadRenderers';
import { humanizeSubjectType } from './subjectType';
import type { TraceNode } from './TraceDrilldown';

function asDisplayString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

// Maps an human review review item + its append-only decision log into the shared
// TraceNode: the typed subject identity → grounding (the source-of-truth row), payload → header
// metadata + artifacts, and the decision log → the timeline. This is the first
// consumer proving the primitive reuses across surfaces; SA3 (task attempts) and
// Assess (audit findings) add their own adapters against the same contract.
export function humanReviewRequestToTrace(
  item: HumanReviewRequest,
  decisions: HumanReviewDecision[]
): TraceNode {
  const metadata: TraceNode['metadata'] = [
    { label: 'Type', value: humanizeSubjectType(item.subjectType) },
    { label: 'Priority', value: item.priority || 'P2' },
    { label: 'State', value: item.state.replace('_', ' ') },
  ];
  if (item.decisionSource) {
    metadata.push({ label: 'Decided by', value: item.decisionSource });
  }
  if (typeof item.confidence === 'number') {
    metadata.push({
      label: 'Confidence',
      value: `${Math.round(item.confidence * 100)}%`,
    });
  }

  const refs: TraceNode['refs'] = Object.entries(
    humanReviewSubjectRef(item)
  ).map(([key, value]) => ({ label: key, location: asDisplayString(value) }));

  const artifacts: TraceNode['artifacts'] = Object.entries(item.payload).map(
    ([key, value]) => ({ label: key, value: asDisplayString(value) })
  );

  const events: TraceNode['events'] = decisions.map((decision) => ({
    at: decision.decidedAt,
    title: decision.result.replace('_', ' '),
    detail: decision.reason
      ? `${humanReviewDecisionActor(decision)}: ${decision.reason}`
      : humanReviewDecisionActor(decision),
    badge: {
      label: `#${decision.sequence}`,
      variant: decisionResultBadgeVariant(decision.result),
    },
  }));

  return {
    title: payloadTitle(item),
    metadata,
    events,
    artifacts,
    secondary: {
      label: 'References and raw data',
      refs,
      raw: { subjectRef: humanReviewSubjectRef(item), payload: item.payload },
    },
  };
}

export function taskAttemptToTrace(attempt: Attempt): TraceNode {
  const metadata: TraceNode['metadata'] = [
    { label: 'State', value: attempt.state.replaceAll('_', ' ') },
    ...(attempt.conclusion
      ? [
          {
            label: 'Conclusion',
            value: attempt.conclusion.replaceAll('_', ' '),
          },
        ]
      : []),
    { label: 'Task', value: attempt.taskId },
    { label: 'Epic', value: attempt.epicId },
    { label: 'Repository', value: attempt.repositoryId },
    { label: 'Assignee', value: attempt.workerId || 'Unassigned' },
    { label: 'Attempt', value: String(attempt.iteration + 1) },
    { label: 'Cost', value: `$${attempt.costUsd.toFixed(2)}` },
    {
      label: 'Usage',
      value: `${attempt.inputTokens.toLocaleString()} input · ${attempt.outputTokens.toLocaleString()} output`,
    },
    { label: 'Run time', value: formatRunDuration(attempt.runDurationMs) },
    { label: 'Created', value: attempt.createdAt },
    { label: 'Updated', value: attempt.updatedAt },
    ...(attempt.result
      ? [{ label: 'Result', value: attempt.result, wide: true }]
      : []),
    ...(attempt.conclusionReason
      ? [
          {
            label: 'Conclusion reason',
            value: attempt.conclusionReason,
            full: true,
          },
        ]
      : []),
    {
      label: 'Worktree',
      value: attempt.worktreePath || 'Not leased',
      full: true,
      nowrap: true,
    },
  ];

  const events: TraceNode['events'] = [
    ...(attempt.priorFeedback
      ? [
          {
            title: 'Prior feedback',
            detail: attempt.priorFeedback,
            badge: {
              label: `Attempt ${attempt.iteration + 1}`,
              variant: 'orange' as const,
            },
          },
        ]
      : []),
  ];

  const refs: TraceNode['refs'] = [];
  if (attempt.prUrl) {
    refs.push({ label: 'Pull request', href: attempt.prUrl });
  }
  if (attempt.commitSha) {
    refs.push({ label: 'Commit', location: attempt.commitSha });
  }

  return {
    title: `Attempt ${attempt.attemptId}`,
    metadata,
    events,
    refs,
    raw: attempt,
  };
}

const MILLISECONDS_PER_SECOND = 1_000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

export function formatRunDuration(durationMs: number | undefined): string {
  if (
    durationMs === undefined ||
    !Number.isFinite(durationMs) ||
    durationMs < 0
  ) {
    return 'Unavailable';
  }
  if (durationMs < MILLISECONDS_PER_SECOND) {
    return `${durationMs} ms`;
  }
  const totalSeconds = Math.floor(durationMs / MILLISECONDS_PER_SECOND);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const totalMinutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  return hours > 0
    ? `${hours}h ${minutes}m ${seconds}s`
    : `${minutes}m ${seconds}s`;
}
